import clsx from 'clsx';
import * as React from 'react';
import { IconButton } from '@/components/ui/iconButton';

export type AlertSeverity = 'error' | 'info' | 'success' | 'warning';
export type AlertVariant = 'standard' | 'outlined';

const standardColorClasses: Record<AlertSeverity, string> = {
  error:
    'text-(--mui-palette-_components-alert-error-color) bg-(--mui-palette-_components-alert-error-background)',
  info: 'text-(--mui-palette-_components-alert-info-color) bg-(--mui-palette-_components-alert-info-background)',
  success:
    'text-(--mui-palette-_components-alert-success-color) bg-(--mui-palette-_components-alert-success-background)',
  warning:
    'text-(--mui-palette-_components-alert-warning-color) bg-(--mui-palette-_components-alert-warning-background)'
};

const outlinedColorClasses: Record<AlertSeverity, string> = {
  error: 'text-(--mui-palette-Alert-errorColor) border-(--mui-palette-error-light)',
  info: 'text-(--mui-palette-Alert-infoColor) border-(--mui-palette-info-light)',
  success: 'text-(--mui-palette-Alert-successColor) border-(--mui-palette-success-light)',
  warning: 'text-(--mui-palette-Alert-warningColor) border-(--mui-palette-warning-light)'
};

const iconColorClasses: Record<AlertSeverity, string> = {
  error: 'text-(--mui-palette-Alert-errorIconColor)',
  info: 'text-(--mui-palette-Alert-infoIconColor)',
  success: 'text-(--mui-palette-Alert-successIconColor)',
  warning: 'text-(--mui-palette-Alert-warningIconColor)'
};

const SvgIcon = ({ path }: { path: string }) => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor">
    <path d={path} />
  </svg>
);

const defaultIcons: Record<AlertSeverity, React.ReactNode> = {
  success: (
    <SvgIcon path="M20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4C12.76,4 13.5,4.11 14.2, 4.31L15.77,2.74C14.61,2.26 13.34,2 12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0, 0 22,12M7.91,10.08L6.5,11.5L11,16L21,6L19.59,4.58L11,13.17L7.91,10.08Z" />
  ),
  warning: (
    <SvgIcon path="M12 5.99L19.53 19H4.47L12 5.99M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z" />
  ),
  error: (
    <SvgIcon path="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
  ),
  info: (
    <SvgIcon path="M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20, 12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10, 10 0 0,0 12,2M11,17H13V11H11V17Z" />
  )
};

const CloseIcon = () => (
  <SvgIcon path="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
);

export interface AlertProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'color'> {
  action?: React.ReactNode;
  icon?: React.ReactNode | false;
  onClose?: () => void;
  severity?: AlertSeverity;
  variant?: AlertVariant;
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  {
    action,
    children,
    className,
    icon,
    onClose,
    severity = 'success',
    variant = 'standard',
    ...rest
  },
  ref
) {
  return (
    <div
      ref={ref}
      role="alert"
      className={clsx(
        'flex rounded px-4 py-[6px]',
        'text-[0.875rem] font-normal leading-[1.43] tracking-[0.01071em] font-(family-name:--font-manrope)',
        variant === 'standard' && standardColorClasses[severity],
        variant === 'outlined' && [
          'bg-transparent border border-solid',
          outlinedColorClasses[severity]
        ],
        className
      )}
      {...rest}
    >
      {icon !== false && (
        <div
          className={clsx('flex mr-3 py-[7px] text-[22px] opacity-90', iconColorClasses[severity])}
        >
          {icon ?? defaultIcons[severity]}
        </div>
      )}
      <div className="min-w-0 overflow-auto py-2">{children}</div>
      {action != null ? (
        <div className="flex items-start pt-1 pl-4 -mr-2 ml-auto">{action}</div>
      ) : (
        onClose && (
          <div className="flex items-start pt-1 pl-4 -mr-2 ml-auto">
            <IconButton
              aria-label="Close"
              title="Close"
              size="small"
              color="inherit"
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </div>
        )
      )}
    </div>
  );
});

export interface AlertTitleProps extends React.ComponentPropsWithoutRef<'div'> {}

export const AlertTitle = React.forwardRef<HTMLDivElement, AlertTitleProps>(function AlertTitle(
  { className, ...rest },
  ref
) {
  return (
    <div
      ref={ref}
      className={clsx(
        'block -mt-0.5 text-base font-medium leading-normal tracking-[0.00938em] font-(family-name:--font-manrope)',
        className
      )}
      {...rest}
    />
  );
});
